/**
 * Goal Filters Component
 *
 * Provides filtering options for the goals list.
 */

import type { GoalFilters, GoalGroupBy, GoalStatus, GoalType } from '@/types';

interface GoalFiltersBarProps {
  filters: GoalFilters;
  onFiltersChange: (filters: GoalFilters) => void;
  groupBy?: GoalGroupBy;
  onGroupByChange?: (groupBy: GoalGroupBy | undefined) => void;
  showCompleted?: boolean;
  onShowCompletedChange?: (show: boolean) => void;
}

export function GoalFiltersBar({
  filters,
  onFiltersChange,
  groupBy,
  onGroupByChange,
  showCompleted = false,
  onShowCompletedChange,
}: GoalFiltersBarProps) {
  const handleStatusChange = (status: GoalStatus | '') => {
    onFiltersChange({
      ...filters,
      status: status || undefined,
    });
  };

  const handleTypeChange = (goalType: GoalType | '') => {
    onFiltersChange({
      ...filters,
      goalType: goalType || undefined,
    });
  };

  const handleSearchChange = (search: string) => {
    onFiltersChange({
      ...filters,
      search: search || undefined,
    });
  };

  const clearFilters = () => {
    onFiltersChange({});
    if (onGroupByChange) {
      onGroupByChange(undefined);
    }
  };

  const hasFilters = filters.status || filters.goalType || filters.search;

  return (
    <div className="space-y-3">
      {/* Search and Group By */}
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search goals..."
              value={filters.search || ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        {/* Group By */}
        {onGroupByChange && (
          <select
            value={groupBy || ''}
            onChange={(e) => onGroupByChange(e.target.value as GoalGroupBy | undefined || undefined)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">No grouping</option>
            <option value="status">Group by Status</option>
            <option value="type">Group by Type</option>
            <option value="targetDate">Group by Due Date</option>
          </select>
        )}
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Status filter */}
        <select
          value={filters.status || ''}
          onChange={(e) => handleStatusChange(e.target.value as GoalStatus | '')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">All Status</option>
          <option value="active">🎯 Active</option>
          <option value="paused">⏸️ Paused</option>
          <option value="completed">✅ Completed</option>
          <option value="abandoned">❌ Abandoned</option>
        </select>

        {/* Type filter */}
        <select
          value={filters.goalType || ''}
          onChange={(e) => handleTypeChange(e.target.value as GoalType | '')}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">All Types</option>
          <option value="outcome">🎯 Outcomes</option>
          <option value="process">🔄 Processes</option>
          <option value="milestone">🏁 Milestones</option>
        </select>

        {/* Show completed toggle */}
        {onShowCompletedChange && (
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => onShowCompletedChange(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Show completed
          </label>
        )}

        {/* Clear filters */}
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

